import { Dialog } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';
import { LicensedProductDto } from 'src/app/models/license.model';
import { AlertService } from 'src/app/services/alert.service';
import { LicensedProductStore } from 'src/app/services/licensed-product.store';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';
import { matchesSearch } from 'src/app/utils/text-search';

@Component({
  selector: 'app-license-product-list',
  templateUrl: './license-product-list.component.html',
  styleUrls: ['./license-product-list.component.scss'],
  standalone: false,
})
export class LicenseProductListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly alertService = inject(AlertService);
  protected readonly licensedProductStore = inject(LicensedProductStore);
  private readonly profile = toSignal(inject(Store).select(selectProfileState));

  searchText = '';

  /** A support user reads the products; only a masteradmin may change one. */
  get canManageProducts(): boolean {
    return this.profile()?.isMasteradmin ?? false;
  }

  constructor() {
    effect(() => {
      const error = this.licensedProductStore.error();
      if (!error) {
        return;
      }

      this.alertService.error(this.getErrorMessage(error));
      this.licensedProductStore.clearError();
    });
  }

  ngOnInit(): void {
    this.licensedProductStore.getAllProducts();
  }

  get filteredProducts(): LicensedProductDto[] {
    return this.licensedProductStore
      .products()
      .filter((product) =>
        matchesSearch(this.searchText, [
          product.id,
          product.name,
          product.description,
          product.target,
          ...(product.licensedComponents ?? []).flatMap((component) => [
            component.id,
            component.componentName,
            component.description,
            component.kind,
          ]),
        ]),
      );
  }

  onSearchChange(searchText: string): void {
    this.searchText = searchText;
  }

  navigateToCreateProduct(): void {
    this.router.navigate(['/admin/licenses/products/create']);
  }

  navigateToEditProduct(productId: string): void {
    this.router.navigate(['/admin/licenses/products', productId, 'edit']);
  }

  deleteProduct(productId: string): void {
    const product =
      this.licensedProductStore
        .products()
        .find((existingProduct) => existingProduct.id === productId) ?? null;
    const productLabel = product?.name?.trim() || 'this licensed product';

    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Delete Licensed Product',
        bodyText: `Are you sure you want to delete "${productLabel}"?\n\nThis action cannot be undone.`,
        cancelable: true,
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      const dialogResult = result as { cancel?: boolean } | undefined;
      if (dialogResult?.cancel === false) {
        this.licensedProductStore.deleteProduct(productId);
      }
    });
  }

  getErrorMessage(error: unknown): string {
    if (!error) {
      return '';
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 409) {
        return 'This licensed product is currently in use and cannot be deleted.';
      }
      if (typeof error.error === 'string' && error.error.trim()) {
        return error.error.trim();
      }
      if (
        typeof error.error === 'object' &&
        error.error &&
        typeof (error.error as { message?: unknown }).message === 'string'
      ) {
        return (error.error as { message: string }).message;
      }

      return 'Failed to load licensed products.';
    }

    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        message?: string;
        error?: { message?: string };
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }

    return 'Failed to load licensed products.';
  }

  trackByProduct(index: number, product: LicensedProductDto): string {
    return product.id ?? `${product.name ?? 'licensed-product'}-${index}`;
  }
}
